import { useState, useCallback, useMemo, useEffect } from 'react';
import { Card, CardContent } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Badge';
import { Dialog } from '@/components/ui/Dialog';
import { Select } from '@/components/ui/Select';
import { Input } from '@/components/ui/Input';
import {
  DataTable,
  type ColumnDef,
  type PaginationState,
  type SortState,
} from '@/components/data-table';
import { ReportBuilder } from './ReportBuilder';
import { ReportResultsModal, type ReportResultsModalPayload } from './ReportResultsModal';
import {
  DATA_SOURCE_OPTIONS,
  type ReportBuilderQuery,
  type ReportBuilderResult,
  type SavedBuilderReport,
} from './hooks/useReportBuilder';
import {
  useSavedReportsList,
  useDeleteSavedReport,
  useRunLegacyReport,
  REPORT_TYPES,
  type SavedReport,
  type GenerateReportParams,
} from './hooks/useReports';
import { useReportBuilderPreview } from './hooks/useReportBuilder';
import { usePinnedReportIds, usePinReport, useUnpinReport } from './hooks/usePins';
import { exportBuilderResultToExcel } from './reportResultsUtils';
import {
  useDocumentTemplates,
  useGenerateReportPdf,
  usePreviewReportPdf,
} from '@/features/document-templates/hooks/useDocumentTemplates';
import { TemplatePreview } from '@/features/document-templates/components/TemplatePreview';
import {
  canExportReports,
  canGenerateReports,
  canRunReports,
  canViewReportFinancials,
  canViewReportsList,
  canAccessReportsModule,
  filterAllowedReportSources,
} from './reportingAccess';
import { usePermissions } from '@/hooks/usePermissions';
import { useDebounce } from '@/hooks/useDebounce';

type ReportsView = 'list' | 'edit';

type EditContext =
  | { mode: 'new' }
  | { mode: 'edit'; report: SavedReport; initialQuery?: ReportBuilderQuery };

type ResultsModalState = {
  open: boolean;
  title: string;
  reportId?: string;
  loading: boolean;
  error: string | null;
  payload: ReportResultsModalPayload | null;
  legacyParams: GenerateReportParams | null;
};

const initialModalState: ResultsModalState = {
  open: false,
  title: '',
  loading: false,
  error: null,
  payload: null,
  legacyParams: null,
};

const SOURCE_FILTER_OPTIONS = DATA_SOURCE_OPTIONS.map((ds) => ({
  value: ds.value,
  label: ds.label,
}));

function reportDataSource(report: SavedReport): string | undefined {
  const src = report.query?.source;
  return typeof src === 'string' ? src : undefined;
}

function reportSourceLabel(report: SavedReport): string {
  const src = reportDataSource(report);
  if (src) {
    return DATA_SOURCE_OPTIONS.find((d) => d.value === src)?.label ?? src;
  }
  if (report.reportType === 'custom') return '—';
  const category = REPORT_TYPES.find((rt) => rt.value === report.reportType)?.category;
  if (!category) return '—';
  return category.charAt(0).toUpperCase() + category.slice(1);
}

function formatReportDate(value?: string | null): string {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleDateString('en-CA');
}

export function ReportsPage() {
  const { can, canAny, isAdmin } = usePermissions();
  const access = { isAdmin, can, canAny };
  const canAccess = canAccessReportsModule(access);
  const canViewList = canViewReportsList(access);
  const canRun = canRunReports(access);
  const canGenerate = canGenerateReports(access);
  const canExport = canExportReports(access);
  const canFinancials = canViewReportFinancials(access);

  const [view, setView] = useState<ReportsView>('list');
  const [editContext, setEditContext] = useState<EditContext>({ mode: 'new' });
  const [modal, setModal] = useState<ResultsModalState>(initialModalState);
  const [deletingReport, setDeletingReport] = useState<SavedReport | null>(null);
  const [templatePreview, setTemplatePreview] = useState<{ open: boolean; html: string }>({
    open: false,
    html: '',
  });
  const [search, setSearch] = useState('');
  const [sourceFilter, setSourceFilter] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [sort, setSort] = useState<SortState>({ column: 'updatedAt', direction: 'desc' });
  const debouncedSearch = useDebounce(search, 300);
  const searchQuery = debouncedSearch.trim().length >= 3 ? debouncedSearch.trim() : '';

  useEffect(() => {
    setPage(1);
  }, [searchQuery, sourceFilter]);

  const { data: savedList, isLoading: savedLoading } = useSavedReportsList({
    enabled: canViewList,
    search: searchQuery,
    source: sourceFilter || undefined,
    page,
    pageSize,
    sortBy: sort.column,
    sortDirection: sort.direction ?? 'desc',
  });
  const savedReports = useMemo(() => savedList?.reports ?? [], [savedList]);
  const deleteReport = useDeleteSavedReport();
  const previewMutation = useReportBuilderPreview();
  const legacyRunMutation = useRunLegacyReport();

  // Report templates: used to decide which reports have a printable template.
  const { data: reportTemplates = [] } = useDocumentTemplates({ category: 'report' });
  // Reports that have at least one report template bound to them. When multiple
  // templates target the same report, the default one is preferred (server side).
  const reportsWithTemplate = useMemo(() => {
    const set = new Set<string>();
    for (const t of reportTemplates) {
      if (t.report_saved_report_id) set.add(t.report_saved_report_id);
    }
    return set;
  }, [reportTemplates]);
  const generateReportPdf = useGenerateReportPdf();
  const previewReportPdf = usePreviewReportPdf();

  const handlePreviewTemplate = useCallback(
    async (report: SavedReport) => {
      setTemplatePreview({ open: true, html: '' });
      try {
        // No explicit templateId — let the server pick the default report
        // template (bound-default → tenant default → any).
        const { html } = await previewReportPdf.mutateAsync({
          savedReportId: report.id,
        });
        setTemplatePreview({ open: true, html });
      } catch {
        setTemplatePreview({ open: false, html: '' });
      }
    },
    [previewReportPdf],
  );

  const handleDownloadReportPdf = useCallback(
    async (report: SavedReport) => {
      try {
        // No explicit templateId — server resolves the default report template.
        const result = await generateReportPdf.mutateAsync({
          savedReportId: report.id,
        });
        if (result.downloadUrl) {
          window.open(result.downloadUrl, '_blank', 'noopener');
        }
      } catch {
        // Errors surface via the mutation state; no-op here.
      }
    },
    [generateReportPdf],
  );

  const closeModal = useCallback(() => setModal(initialModalState), []);

  const handleConfirmDelete = useCallback(() => {
    if (!deletingReport) return;
    deleteReport.mutate(deletingReport.id, {
      onSuccess: () => {
        setDeletingReport(null);
        if (modal.reportId === deletingReport.id) {
          closeModal();
        }
      },
    });
  }, [deletingReport, deleteReport, modal.reportId, closeModal]);

  const openBuilderResult = useCallback(
    (title: string, result: ReportBuilderResult, reportId?: string) => {
      setModal({
        open: true,
        title,
        reportId,
        loading: false,
        error: null,
        payload: { kind: 'builder', result },
        legacyParams: null,
      });
    },
    [],
  );

  const runCustomQuery = useCallback(
    async (title: string, query: ReportBuilderQuery, reportId?: string) => {
      setModal({
        open: true,
        title,
        reportId,
        loading: true,
        error: null,
        payload: null,
        legacyParams: null,
      });
      try {
        const result = await previewMutation.mutateAsync(query);
        openBuilderResult(title, result, reportId);
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Failed to run report';
        setModal((m) => ({ ...m, loading: false, error: message }));
      }
    },
    [previewMutation, openBuilderResult],
  );

  const runLegacyReport = useCallback(
    async (saved: SavedReport, page = 1) => {
      const params: GenerateReportParams = {
        reportType: saved.reportType,
        filters: saved.filters,
        columns: saved.columns?.map((c) => c.id),
        page,
        pageSize: 50,
      };
      setModal({
        open: true,
        title: saved.name,
        reportId: saved.id,
        loading: true,
        error: null,
        payload: null,
        legacyParams: params,
      });
      try {
        const result = await legacyRunMutation.mutateAsync(params);
        setModal({
          open: true,
          title: saved.name,
          reportId: saved.id,
          loading: false,
          error: null,
          payload: { kind: 'legacy', result, page },
          legacyParams: params,
        });
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Failed to run report';
        setModal((m) => ({ ...m, loading: false, error: message }));
      }
    },
    [legacyRunMutation],
  );

  const handleRunSaved = useCallback(
    (saved: SavedReport) => {
      if (!canRun) return;
      if (saved.reportType === 'custom' && saved.query) {
        void runCustomQuery(saved.name, saved.query as unknown as ReportBuilderQuery, saved.id);
      } else if (saved.reportType !== 'custom') {
        void runLegacyReport(saved, 1);
      }
    },
    [canRun, runCustomQuery, runLegacyReport],
  );

  const handleEditSaved = useCallback(
    (saved: SavedReport) => {
      if (!canGenerate) return;
      if (saved.reportType === 'custom' && saved.query) {
        setEditContext({
          mode: 'edit',
          report: saved,
          initialQuery: saved.query as unknown as ReportBuilderQuery,
        });
      } else {
        setEditContext({ mode: 'edit', report: saved });
      }
      setView('edit');
    },
    [canGenerate],
  );

  const handleNewReport = useCallback(() => {
    if (!canGenerate) return;
    setEditContext({ mode: 'new' });
    setView('edit');
  }, [canGenerate]);

  const handleBackToList = useCallback(() => {
    setView('list');
    setEditContext({ mode: 'new' });
  }, []);

  const handleBuilderRunComplete = useCallback(
    (result: ReportBuilderResult, reportName?: string) => {
      openBuilderResult(
        reportName || (editContext.mode === 'edit' ? editContext.report.name : 'Report preview'),
        result,
        editContext.mode === 'edit' ? editContext.report.id : undefined,
      );
    },
    [editContext, openBuilderResult],
  );

  const handleBuilderSaveComplete = useCallback((saved: SavedBuilderReport) => {
    setEditContext({
      mode: 'edit',
      report: {
        id: saved.id,
        name: saved.name,
        reportType: 'custom',
        filters: {},
        columns: [],
        query: saved.query as unknown as Record<string, unknown>,
        isScheduled: false,
        createdAt: saved.createdAt,
        updatedAt: saved.updatedAt,
        ownerName: null,
      },
      initialQuery: saved.query,
    });
    setView('edit');
  }, []);

  const handleModalEdit = useCallback(() => {
    if (!modal.reportId || !savedReports) return;
    const saved = savedReports.find((r) => r.id === modal.reportId);
    if (!saved) return;
    closeModal();
    handleEditSaved(saved);
  }, [modal.reportId, savedReports, closeModal, handleEditSaved]);

  const handleLegacyPageChange = useCallback(
    (page: number) => {
      if (!modal.legacyParams || !modal.reportId || !savedReports) return;
      const saved = savedReports.find((r) => r.id === modal.reportId);
      if (!saved) return;
      void runLegacyReport(saved, page);
    },
    [modal.legacyParams, modal.reportId, savedReports, runLegacyReport],
  );

  const handleModalExport = useCallback(() => {
    if (modal.payload?.kind === 'builder') {
      exportBuilderResultToExcel(modal.payload.result, modal.title);
    }
  }, [modal]);

  if (!canAccess) {
    return (
      <div className="py-12 text-center text-sm text-gray-500 dark:text-gray-400">
        You do not have permission to view reports.
      </div>
    );
  }

  if (!canViewList) {
    return (
      <div className="py-12 text-center text-sm text-gray-500 dark:text-gray-400">
        You do not have permission to view the saved reports list.
      </div>
    );
  }

  if (view === 'edit') {
    const editingReportId = editContext.mode === 'edit' ? editContext.report.id : undefined;
    const editingReportName = editContext.mode === 'edit' ? editContext.report.name : undefined;
    const initialQuery = editContext.mode === 'edit' ? editContext.initialQuery : undefined;

    return (
      <div className="space-y-6">
        <div className="flex items-center gap-3">
          <Button variant="outline" size="sm" onClick={handleBackToList}>
            ← Back to reports
          </Button>
          <h1 className="text-xl font-bold text-gray-900 dark:text-gray-100">
            {editContext.mode === 'edit' ? `Edit: ${editContext.report.name}` : 'New report'}
          </h1>
        </div>

        {!canGenerate ? (
          <Card>
            <CardContent>
              <p className="py-8 text-center text-sm text-gray-500 dark:text-gray-400">
                You do not have permission to create or edit reports.
              </p>
            </CardContent>
          </Card>
        ) : (
          <ReportBuilder
            key={editingReportId ?? 'new'}
            initialQuery={initialQuery}
            editingReportId={editingReportId}
            editingReportName={editingReportName}
            onBack={handleBackToList}
            onRunComplete={handleBuilderRunComplete}
            onSaveComplete={handleBuilderSaveComplete}
            permissions={{
              canRun,
              canGenerate,
              canExport,
              canFinancials,
            }}
          />
        )}

        <ReportResultsModal
          open={modal.open}
          title={modal.title}
          loading={modal.loading}
          error={modal.error}
          payload={modal.payload}
          canExport={canExport}
          canEdit={false}
          onClose={closeModal}
          onExport={handleModalExport}
          onEdit={handleModalEdit}
          onLegacyPageChange={handleLegacyPageChange}
        />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">Reports</h1>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            Saved report configurations for your firm
          </p>
        </div>
        {canGenerate && <Button onClick={handleNewReport}>New report</Button>}
      </div>

      <SavedReportsList
        reports={savedReports}
        isLoading={savedLoading}
        canRun={canRun}
        canGenerate={canGenerate}
        reportsWithTemplate={reportsWithTemplate}
        search={search}
        sourceFilter={sourceFilter}
        sort={sort}
        pagination={
          savedList
            ? { page: savedList.page, pageSize: savedList.pageSize, total: savedList.total }
            : undefined
        }
        onSearchChange={setSearch}
        onSourceChange={setSourceFilter}
        onSortChange={setSort}
        onPageChange={setPage}
        onPageSizeChange={(nextSize) => {
          setPageSize(nextSize);
          setPage(1);
        }}
        onRun={handleRunSaved}
        onEdit={handleEditSaved}
        onDelete={setDeletingReport}
        onPreviewTemplate={handlePreviewTemplate}
        onDownloadPdf={handleDownloadReportPdf}
        isGeneratingPdf={generateReportPdf.isPending}
      />

      {deletingReport && (
        <Dialog
          open
          onClose={() => setDeletingReport(null)}
          onConfirm={handleConfirmDelete}
          title="Delete report"
          description={`Are you sure you want to delete "${deletingReport.name}"? This cannot be undone.`}
          confirmLabel="Delete"
          confirmVariant="danger"
          loading={deleteReport.isPending}
        />
      )}

      <ReportResultsModal
        open={modal.open}
        title={modal.title}
        loading={modal.loading}
        error={modal.error}
        payload={modal.payload}
        canExport={canExport}
        canEdit={canGenerate && !!modal.reportId}
        onClose={closeModal}
        onExport={handleModalExport}
        onEdit={handleModalEdit}
        onLegacyPageChange={handleLegacyPageChange}
      />

      {templatePreview.open && (
        <TemplatePreview
          html={templatePreview.html}
          isLoading={previewReportPdf.isPending}
          onClose={() => setTemplatePreview({ open: false, html: '' })}
        />
      )}
    </div>
  );
}

interface SavedReportsListProps {
  reports: SavedReport[];
  isLoading: boolean;
  canRun: boolean;
  canGenerate: boolean;
  reportsWithTemplate: Set<string>;
  search: string;
  sourceFilter: string;
  sort: SortState;
  pagination?: PaginationState;
  onSearchChange: (value: string) => void;
  onSourceChange: (value: string) => void;
  onSortChange: (sort: SortState) => void;
  onPageChange: (page: number) => void;
  onPageSizeChange: (pageSize: number) => void;
  onRun: (report: SavedReport) => void;
  onEdit: (report: SavedReport) => void;
  onDelete: (report: SavedReport) => void;
  onPreviewTemplate: (report: SavedReport) => void;
  onDownloadPdf: (report: SavedReport) => void;
  isGeneratingPdf: boolean;
}

function SavedReportsList({
  reports,
  isLoading,
  canRun,
  canGenerate,
  reportsWithTemplate,
  search,
  sourceFilter,
  sort,
  pagination,
  onSearchChange,
  onSourceChange,
  onSortChange,
  onPageChange,
  onPageSizeChange,
  onRun,
  onEdit,
  onDelete,
  onPreviewTemplate,
  onDownloadPdf,
  isGeneratingPdf,
}: SavedReportsListProps) {
  const { can, canAny, isAdmin } = usePermissions();
  // Only offer source filters for report groups (modules) the user may access.
  const sourceFilterOptions = useMemo(
    () => filterAllowedReportSources({ isAdmin, can, canAny }, SOURCE_FILTER_OPTIONS),
    [isAdmin, can, canAny],
  );

  // Per-user dashboard pins: badge pinned rows and offer a Pin/Unpin toggle.
  const { data: pinnedIds } = usePinnedReportIds();
  const pinReport = usePinReport();
  const unpinReport = useUnpinReport();
  const pinnedSet = useMemo(() => new Set(pinnedIds ?? []), [pinnedIds]);
  const togglePin = useCallback(
    (report: SavedReport) => {
      if (pinnedSet.has(report.id)) {
        unpinReport.mutate(report.id);
      } else {
        pinReport.mutate(report.id);
      }
    },
    [pinnedSet, pinReport, unpinReport],
  );

  const columns = useMemo<ColumnDef<SavedReport>[]>(
    () => [
      {
        id: 'name',
        header: 'Name',
        accessorKey: 'name',
        sortable: true,
        cell: (report) => (
          <div className="flex flex-wrap items-center gap-2">
            <span className="font-medium text-gray-900 dark:text-gray-100">{report.name}</span>
            {report.isScheduled && (
              <Badge variant="info" size="sm">
                Scheduled ({report.scheduleFrequency})
              </Badge>
            )}
            {reportsWithTemplate.has(report.id) && (
              <Badge variant="success" size="sm">
                Template
              </Badge>
            )}
            {pinnedSet.has(report.id) && (
              <Badge variant="info" size="sm">
                Pinned
              </Badge>
            )}
          </div>
        ),
      },
      {
        id: 'owner',
        header: 'Owner',
        sortable: true,
        cell: (report) => report.ownerName || '—',
      },
      {
        id: 'source',
        header: 'Source',
        sortable: true,
        cell: (report) => reportSourceLabel(report),
      },
      {
        id: 'createdAt',
        header: 'Created',
        accessorKey: 'createdAt',
        sortable: true,
        cell: (report) => formatReportDate(report.createdAt),
      },
      {
        id: 'updatedAt',
        header: 'Last updated',
        accessorKey: 'updatedAt',
        sortable: true,
        cell: (report) => formatReportDate(report.updatedAt),
      },
      {
        id: 'actions',
        header: '',
        align: 'right',
        cell: (report) => {
          const hasTemplate = reportsWithTemplate.has(report.id);
          const actionClass =
            'border-gray-300 bg-white hover:bg-gray-50 dark:border-gray-500 dark:bg-surface-dark-secondary dark:hover:bg-gray-700';
          return (
            <div className="flex flex-wrap items-center justify-end gap-2">
              {canRun && (
                <Button
                  variant="outline"
                  size="sm"
                  className={actionClass}
                  onClick={() => onRun(report)}
                >
                  Run
                </Button>
              )}
              <Button
                variant="outline"
                size="sm"
                className={actionClass}
                onClick={() => togglePin(report)}
                aria-label={pinnedSet.has(report.id) ? 'Unpin from dashboard' : 'Pin to dashboard'}
              >
                {pinnedSet.has(report.id) ? 'Unpin' : 'Pin'}
              </Button>
              {hasTemplate && canRun && (
                <>
                  <Button
                    variant="outline"
                    size="sm"
                    className={actionClass}
                    onClick={() => onPreviewTemplate(report)}
                  >
                    Preview
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    className={actionClass}
                    onClick={() => onDownloadPdf(report)}
                    loading={isGeneratingPdf}
                  >
                    Download PDF
                  </Button>
                </>
              )}
              {canGenerate && (
                <>
                  <Button
                    variant="outline"
                    size="sm"
                    className={actionClass}
                    onClick={() => onEdit(report)}
                  >
                    Edit
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => onDelete(report)}
                    aria-label="Delete report"
                  >
                    <svg
                      className="h-4 w-4 text-gray-400 hover:text-red-500"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                      aria-hidden="true"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                      />
                    </svg>
                  </Button>
                </>
              )}
            </div>
          );
        },
      },
    ],
    [
      canRun,
      canGenerate,
      reportsWithTemplate,
      pinnedSet,
      togglePin,
      onRun,
      onEdit,
      onDelete,
      onPreviewTemplate,
      onDownloadPdf,
      isGeneratingPdf,
    ],
  );

  const emptyMessage =
    !search.trim() && !sourceFilter
      ? canGenerate
        ? 'No saved reports yet. Create a new report to get started.'
        : 'No saved reports yet.'
      : 'No reports match your search.';

  const hasFilters = Boolean(search.trim() || sourceFilter);

  return (
    <div className="space-y-3">
      {!canGenerate && canRun && (
        <p className="text-sm text-gray-500 dark:text-gray-400">
          You can run saved reports but cannot create, edit, or delete them.
        </p>
      )}
      {!canRun && !canGenerate && (
        <p className="text-sm text-gray-500 dark:text-gray-400">
          You can view saved report names but cannot run or change them.
        </p>
      )}
      <Card padding="sm">
        <div className="flex flex-wrap items-end gap-4">
          <div className="min-w-[220px] flex-1 max-w-sm">
            <Input
              type="search"
              label="Search"
              value={search}
              onChange={(e) => onSearchChange(e.target.value)}
              placeholder="Search by name or owner..."
            />
          </div>
          <div className="w-56">
            <Select
              label="Source"
              value={sourceFilter}
              onChange={(e) => onSourceChange(e.target.value)}
              options={sourceFilterOptions}
              placeholder="All sources"
            />
          </div>
          {hasFilters && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                onSearchChange('');
                onSourceChange('');
              }}
            >
              Clear filters
            </Button>
          )}
        </div>
      </Card>
      <DataTable<SavedReport>
        data={reports}
        columns={columns}
        keyExtractor={(row) => row.id}
        sort={sort}
        onSortChange={onSortChange}
        pagination={pagination}
        onPageChange={onPageChange}
        onPageSizeChange={onPageSizeChange}
        loading={isLoading}
        emptyMessage={emptyMessage}
        aria-label="Saved reports"
      />
    </div>
  );
}
